import MapsSearch, { isUnexpected } from '@azure-rest/maps-search';
import { AzureKeyCredential } from '@azure/core-auth';
import { ChannelConfiguration } from '@msdyn365-commerce/retail-proxy';
import { ISuggestion } from '../components/new-checkout/address-input';
import * as Msdyn365 from '@msdyn365-commerce/core';

const withTimeout = <T>(promise: PromiseLike<T>, timeoutMs: number): Promise<T> =>
    new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => reject(new Error('Azure Maps request timed out')), timeoutMs);

        Promise.resolve(promise).then(
            value => {
                clearTimeout(timeoutId);
                resolve(value);
            },
            error => {
                clearTimeout(timeoutId);
                reject(error);
            }
        );
    });

// Create a configured Azure Maps instance
export const createAzureMapsInstance = (
    channel: ChannelConfiguration,
    locale: string = 'en',
    telemetry: Msdyn365.ITelemetry | undefined = undefined
) => {
    const credential = new AzureKeyCredential(channel?.AzureMapsApiKey || '');
    const searchClient = MapsSearch(credential);
    const countryRegionISOCode = channel.ChannelCountryRegionISOCode || '';

    let debounceTimer: NodeJS.Timeout | null = null;
    let previousResolve: ((value: ISuggestion[]) => void) | null = null;
    let previousReject: ((reason?: any) => void) | null = null;

    const getSuggestions = async (query: string, debounceDelay: number = 300): Promise<ISuggestion[]> => {
        // Skip API calls for short queries
        if (query.length < 3) {
            return [];
        }

        return new Promise((resolve, reject) => {
            // Resolve the previous promise to prevent it from hanging
            if (previousResolve) {
                previousResolve([]);
            }

            // Store the current resolve and reject for potential cancellation
            previousResolve = resolve;
            previousReject = reject;

            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }

            debounceTimer = setTimeout(async () => {
                try {
                    const response = await withTimeout(
                        searchClient.path('/search/address/{format}', 'json').get({
                            queryParameters: {
                                query,
                                limit: 5,
                                language: locale,
                                typeahead: true,
                                ...(countryRegionISOCode ? { countrySet: [countryRegionISOCode] } : {})
                            }
                        }),
                        10000
                    );

                    if (isUnexpected(response)) {
                        throw response.body;
                    }

                    // Only resolve if this is still the current request
                    if (previousResolve === resolve) {
                        previousResolve(
                            response.body.results?.map(result => ({
                                formattedSuggestion: result.address?.freeformAddress || '',
                                title: result.address?.freeformAddress || '',
                                subtitle: result.address?.municipality || '',
                                address: result.address ?? {}
                            })) || []
                        );

                        previousResolve = null;
                    }
                } catch (error) {
                    if (telemetry) {
                        telemetry.error('Error fetching suggestions from Azure Maps:', error);
                    }

                    // Only reject if this is still the current request
                    if (previousReject === reject) {
                        previousReject([]);
                        previousReject = null;
                    }
                }
            }, debounceDelay);
        });
    };

    const searchAddress = async (query: string) => {
        try {
            const response = await withTimeout(
                searchClient.path('/search/address/{format}', 'json').get({
                    queryParameters: {
                        query,
                        limit: 1,
                        language: locale,
                        ...(countryRegionISOCode
                            ? {
                                  countrySet: [countryRegionISOCode]
                              }
                            : {})
                    }
                }),
                10000
            );

            if (isUnexpected(response)) {
                throw response.body;
            }

            return response.body;
        } catch (error) {
            if (telemetry) {
                telemetry.error('Error searching address with Azure Maps:', error);
            }

            throw error;
        }
    };

    return {
        searchClient,
        locale,
        countryRegionISOCode,
        getSuggestions,
        searchAddress
    };
};
